import {
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  Cake as CakeIcon,
  Calculate as CalculateIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  DirectionsRun as DirectionsRunIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Height as HeightIcon,
  LocalHospital as LocalHospitalIcon,
  MonitorWeight as MonitorWeightIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Restaurant as RestaurantIcon,
  Save as SaveIcon,
  Scale as ScaleIcon,
  SportsGymnastics as SportsGymnasticsIcon,
  Wc as WcIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Appointment, appointmentApi } from '../services/appointmentApi';
import { Client, clientApi, UpdateClientRequest } from '../services/clientApi';
import { DietProgram, dietProgramApi } from '../services/dietProgramApi';
import { colors } from '../theme';
import {
  BMR_FORMULA_EQUATIONS,
  BMR_PRESETS,
  BMRCoeffs,
  calculateTargetNutrition,
} from '../utils/bmrFormula';
import CancelButton from './CancelButton';
import NutritionTracker from './NutritionTracker';
import WeightTracker from './WeightTracker';

interface ClientDetailModalProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
  onClientUpdated?: () => void;
  initialTab?: 'info' | 'nutrition' | 'weight' | 'appointments' | 'programs';
}

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
  open,
  onClose,
  client,
  onClientUpdated,
  initialTab = 'info',
}) => {
  const { dietitianId, dietitian } = useAuth();
  const { showWarning, showSuccess } = useToast();
  const [bmrCoeffs, setBmrCoeffs] = useState<BMRCoeffs>(() => BMR_PRESETS.mifflin);
  const [bmrPresetLabel, setBmrPresetLabel] = useState<'mifflin' | 'harris-benedict' | 'harris-benedict-revised' | 'custom'>('mifflin');
  const [formulaEditDialogOpen, setFormulaEditDialogOpen] = useState(false);
  const [editCoeffs, setEditCoeffs] = useState<BMRCoeffs>(() => BMR_PRESETS.mifflin);
  const [selectedPresetInDialog, setSelectedPresetInDialog] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    'info' | 'nutrition' | 'weight' | 'appointments' | 'programs'
  >(initialTab);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [dietPrograms, setDietPrograms] = useState<DietProgram[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState<UpdateClientRequest>({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && client) {
      fetchClientData();
      setEditFormData({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        phone: client.phone || '',
        age: client.age,
        gender: client.gender || '',
        weight: client.weight,
        height: client.height,
        goal: client.goal || '',
        activityLevel: client.activityLevel || '',
        dailyCalories: client.dailyCalories,
        dailyProtein: client.dailyProtein,
        dailyCarbs: client.dailyCarbs,
        dailyFat: client.dailyFat,
        notes: client.notes || '',
      });
      setIsEditing(false);
      setActiveTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, client, initialTab]);

  const fetchClientData = async () => {
    if (!client) return;

    try {
      setLoading(true);
      setError(null);
      const [appointmentsData, dietProgramsData] = await Promise.all([
        appointmentApi.getAppointmentsByClient(client.id),
        dietProgramApi.getDietProgramsByClient(client.id),
      ]);
      setAppointments(appointmentsData);
      setDietPrograms(dietProgramsData);
    } catch (err) {
      setError('Veriler yüklenirken hata oluştu');
      console.error('Error fetching client data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openFormulaEditDialog = () => {
    setEditCoeffs({ ...bmrCoeffs });
    setSelectedPresetInDialog(null);
    setFormulaEditDialogOpen(true);
  };

  const applyPresetToEdit = (presetKey: string) => {
    const preset = BMR_PRESETS[presetKey];
    if (preset) {
      setEditCoeffs({ ...preset });
      setSelectedPresetInDialog(presetKey);
    }
  };

  const saveFormulaEdit = () => {
    setBmrCoeffs({ ...editCoeffs });
    setBmrPresetLabel(
      selectedPresetInDialog && selectedPresetInDialog in BMR_PRESETS
        ? (selectedPresetInDialog as typeof bmrPresetLabel)
        : 'custom'
    );
    setFormulaEditDialogOpen(false);
  };

  const calculateNutritionForEdit = () => {
    const age = editFormData.age ?? client?.age ?? 0;
    const weight = editFormData.weight ?? client?.weight ?? 0;
    const height = editFormData.height ?? client?.height ?? 0;
    const gender = editFormData.gender ?? client?.gender ?? '';
    const goal = editFormData.goal ?? client?.goal ?? '';
    const activityLevel = editFormData.activityLevel ?? client?.activityLevel ?? '';
    if (!age || !weight || !height || !gender || !goal || !activityLevel) {
      showWarning(
        'Otomatik hesaplama için yaş, kilo, boy, cinsiyet, hedef ve aktivite seviyesi gereklidir.',
        'Eksik Bilgiler'
      );
      return;
    }
    const result = calculateTargetNutrition(
      { age: Number(age), weight: Number(weight), height: Number(height), gender: String(gender), goal: String(goal), activityLevel: String(activityLevel) },
      bmrCoeffs
    );
    setEditFormData((prev) => ({
      ...prev,
      dailyCalories: result.dailyCalories,
      dailyProtein: result.dailyProtein,
      dailyCarbs: result.dailyCarbs,
      dailyFat: result.dailyFat,
    }));
    showSuccess('Günlük kalori ve makro hesaplandı.', 'Hesaplama Başarılı');
  };

  const handleSave = async () => {
    if (!client) return;

    try {
      setSaving(true);
      setError(null);
      await clientApi.updateClient(client.id, editFormData);
      setIsEditing(false);
      // Refresh client data
      const updatedClient = await clientApi.getClientById(client.id);
      setEditFormData({
        dietitianEmail: updatedClient.dietitianEmail || '',
        dietitianName: updatedClient.dietitianName || '',
      });
      // Trigger parent to refresh
      if (onClientUpdated) {
        onClientUpdated();
      }
    } catch (err) {
      setError('Bilgiler güncellenirken hata oluştu');
      console.error('Error updating client:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (client) {
      setEditFormData({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        phone: client.phone || '',
        age: client.age,
        gender: client.gender || '',
        weight: client.weight,
        height: client.height,
        goal: client.goal || '',
        activityLevel: client.activityLevel || '',
        dailyCalories: client.dailyCalories,
        dailyProtein: client.dailyProtein,
        dailyCarbs: client.dailyCarbs,
        dailyFat: client.dailyFat,
        notes: client.notes || '',
      });
    }
    setIsEditing(false);
  };

  if (!client) return null;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '850px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <PersonIcon sx={{ color: colors.primary.main }} />
            <Typography variant="h5" sx={{ color: colors.text.primary }}>
              {client.firstName} {client.lastName}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: colors.text.secondary,
              width: '20px',

              '&:hover': {
                bgcolor: colors.hover.background,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tab Navigation */}
        <Paper elevation={1} sx={{ mb: 3, background: '#F9FAF7' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#43A047',
              },
              '& .MuiTab-root': {
                color: '#607D8B',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(165, 214, 167, 0.08)',
                  color: '#2E7D32',
                },
                '&.Mui-selected': {
                  color: '#2E7D32',
                  backgroundColor: 'rgba(165, 214, 167, 0.12)',
                },
              },
              '& .MuiTabs-scrollButtons': {
                color: '#43A047',
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            <Tab
              label="Bilgiler"
              value="info"
              icon={<PersonIcon />}
              iconPosition="start"
            />
            <Tab
              label="Yemek Takibi"
              value="nutrition"
              icon={<RestaurantIcon />}
              iconPosition="start"
            />
            <Tab
              label="Kilo Takibi"
              value="weight"
              icon={<MonitorWeightIcon />}
              iconPosition="start"
            />
            <Tab
              label="Randevular"
              value="appointments"
              icon={<CalendarIcon />}
              iconPosition="start"
            />
            <Tab
              label="Diyet Programları"
              value="programs"
              icon={<AssignmentIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress sx={{ color: '#43A047' }} />
          </Box>
        )}

        {!loading && activeTab === 'info' && (
          <Box
            sx={{
              animation: 'fadeIn 0.3s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {/* Header with Edit Button */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography
                variant="h6"
                sx={{ color: '#263238', fontWeight: 600 }}
              >
                Danışan Bilgileri
              </Typography>
              {!isEditing && (
                <Button
                  onClick={() => setIsEditing(true)}
                  startIcon={<EditIcon />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: '#43A047',
                    color: '#43A047',
                    width: 'fit-content',
                    '&:hover': {
                      borderColor: '#388E3C',
                      backgroundColor: 'rgba(67, 160, 71, 0.04)',
                    },
                  }}
                >
                  Düzenle
                </Button>
              )}
            </Box>

            {/* Kişisel Bilgiler Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e8e8e8',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <PersonIcon sx={{ color: '#43A047', fontSize: 24 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#263238', fontWeight: 600 }}
                >
                  Kişisel Bilgiler
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 3,
                }}
              >
                {/* Ad */}
                <TextField
                  fullWidth
                  label="Ad"
                  value={
                    isEditing ? editFormData.firstName || '' : client.firstName
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      firstName: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Soyad */}
                <TextField
                  fullWidth
                  label="Soyad"
                  value={
                    isEditing ? editFormData.lastName || '' : client.lastName
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      lastName: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* E-posta (readonly) */}
                <TextField
                  fullWidth
                  label="E-posta"
                  value={client.email}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Telefon */}
                <TextField
                  fullWidth
                  label="Telefon"
                  value={
                    isEditing ? editFormData.phone || '' : client.phone || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      phone: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Yaş */}
                <TextField
                  fullWidth
                  label="Yaş"
                  type="number"
                  value={isEditing ? editFormData.age || '' : client.age || ''}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      age: parseInt(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CakeIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Cinsiyet */}
                <TextField
                  fullWidth
                  label="Cinsiyet"
                  select
                  value={
                    isEditing ? editFormData.gender || '' : client.gender || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      gender: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WcIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                >
                  <MenuItem value="Erkek">Erkek</MenuItem>
                  <MenuItem value="Kadın">Kadın</MenuItem>
                </TextField>

                {/* Kilo */}
                <TextField
                  fullWidth
                  label="Kilo"
                  type="number"
                  value={
                    isEditing ? editFormData.weight || '' : client.weight || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      weight: parseFloat(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ScaleIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary">
                          kg
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Boy */}
                <TextField
                  fullWidth
                  label="Boy"
                  type="number"
                  value={
                    isEditing ? editFormData.height || '' : client.height || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      height: parseFloat(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HeightIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="body2" color="text.secondary">
                          cm
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Hedef ve Aktivite Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e8e8e8',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SportsGymnasticsIcon sx={{ color: '#43A047', fontSize: 24 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#263238', fontWeight: 600 }}
                >
                  Hedef ve Aktivite
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                {/* Hedef */}
                <TextField
                  fullWidth
                  label="Hedef"
                  select
                  value={
                    isEditing ? editFormData.goal || '' : client.goal || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      goal: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SportsGymnasticsIcon
                          sx={{ color: '#9e9e9e', fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                >
                  <MenuItem value="Kilo verme">Kilo Verme</MenuItem>
                  <MenuItem value="Kilo alma">Kilo Alma</MenuItem>
                  <MenuItem value="Kas kazanma">Kas Kazanma</MenuItem>
                  <MenuItem value="Kilo koruma">Kilo Koruma</MenuItem>
                  <MenuItem value="Sağlıklı beslenme">
                    Sağlıklı Beslenme
                  </MenuItem>
                </TextField>

                {/* Aktivite Seviyesi */}
                <TextField
                  fullWidth
                  label="Aktivite Seviyesi"
                  select
                  value={
                    isEditing
                      ? editFormData.activityLevel || ''
                      : client.activityLevel || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      activityLevel: e.target.value,
                    })
                  }
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DirectionsRunIcon
                          sx={{ color: '#9e9e9e', fontSize: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#f5f5f5',
                      '&:hover': {
                        backgroundColor: '#eeeeee',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                >
                  <MenuItem value="sedanter">
                    Hareketsiz (Egzersiz yok)
                  </MenuItem>
                  <MenuItem value="hafif">
                    Hafif Aktif (Haftada 1-3 gün)
                  </MenuItem>
                  <MenuItem value="orta">Orta Aktif (Haftada 3-5 gün)</MenuItem>
                  <MenuItem value="agir">Çok Aktif (Haftada 6-7 gün)</MenuItem>
                  <MenuItem value="profesyonel">Profesyonel Sporcu</MenuItem>
                </TextField>
              </Box>
            </Paper>

            {/* Kalori ve Makro Hesaplama — sadece düzenleme modunda */}
            {isEditing && (
              <Paper
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  mb: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: 'rgba(255, 152, 0, 0.08)',
                    borderLeft: '4px solid',
                    borderLeftColor: '#FF9800',
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="#E65100">
                    Kalori ve Makro Hesaplama
                  </Typography>
                </Box>
                <Stack spacing={1.5} sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                      BMR formülü:
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {bmrPresetLabel === 'mifflin' && 'Mifflin-St Jeor (1990)'}
                      {bmrPresetLabel === 'harris-benedict' && 'Harris-Benedict Orijinal (1919)'}
                      {bmrPresetLabel === 'harris-benedict-revised' && 'Harris-Benedict Revize (1984)'}
                      {bmrPresetLabel === 'custom' && 'Özel formül'}
                    </Typography>
                    <Tooltip title="Formül katsayılarını düzenle">
                      <IconButton
                        size="small"
                        onClick={openFormulaEditDialog}
                        sx={{
                          color: '#FF9800',
                          border: '1px solid',
                          width: 'fit-content',
                          borderColor: 'rgba(255, 152, 0, 0.5)',
                          bgcolor: 'rgba(255, 152, 0, 0.06)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 152, 0, 0.12)',
                            borderColor: '#FF9800',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ textAlign: 'center', pt: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CalculateIcon />}
                      onClick={calculateNutritionForEdit}
                      sx={{
                        bgcolor: '#FF9800',
                        color: 'white',
                        fontWeight: 600,
                        py: 1,
                        px: 2.5,
                        borderRadius: 1.5,
                        boxShadow: '0 2px 10px rgba(255, 152, 0, 0.3)',
                        '&:hover': { bgcolor: '#F57C00', boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)' },
                      }}
                    >
                      Otomatik Hesapla
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" mt={0.75}>
                      Günlük kalori ve makro formüle göre hesaplanacak.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Günlük Beslenme Hedefleri Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: '#f8f9fa',
                borderRadius: 3,
                border: '1px solid #e8e8e8',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <BarChartIcon sx={{ color: '#43A047', fontSize: 24 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#263238', fontWeight: 600 }}
                >
                  Günlük Beslenme Hedefleri
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                {/* Kalori */}
                <TextField
                  fullWidth
                  label="Kalori (kcal)"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.dailyCalories || ''
                      : client.dailyCalories || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      dailyCalories: parseFloat(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Protein */}
                <TextField
                  fullWidth
                  label="Protein (g)"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.dailyProtein || ''
                      : client.dailyProtein || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      dailyProtein: parseFloat(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Karbonhidrat */}
                <TextField
                  fullWidth
                  label="Karbonhidrat (g)"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.dailyCarbs || ''
                      : client.dailyCarbs || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      dailyCarbs: parseFloat(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />

                {/* Yağ */}
                <TextField
                  fullWidth
                  label="Yağ (g)"
                  type="number"
                  value={
                    isEditing
                      ? editFormData.dailyFat || ''
                      : client.dailyFat || ''
                  }
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      dailyFat: parseFloat(e.target.value) || undefined,
                    })
                  }
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#ffffff',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#fafafa',
                      },
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Notlar Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: '#ffffff',
                borderRadius: 3,
                border: '1px solid #e8e8e8',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <NotesIcon sx={{ color: '#43A047', fontSize: 24 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#263238', fontWeight: 600 }}
                >
                  Notlar
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={
                  isEditing ? editFormData.notes || '' : client.notes || ''
                }
                onChange={(e) =>
                  setEditFormData({
                    ...editFormData,
                    notes: e.target.value,
                  })
                }
                disabled={!isEditing}
                placeholder="Ek notlar..."
                variant={isEditing ? 'outlined' : 'filled'}
                sx={{
                  '& .MuiFilledInput-root': {
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      backgroundColor: '#eeeeee',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#fafafa',
                    },
                  },
                }}
              />
            </Paper>

            {/* Diyetisyen Bilgileri Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                bgcolor: '#f0f7f0',
                borderRadius: 3,
                border: '1px solid #c8e6c9',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <LocalHospitalIcon sx={{ color: '#43A047', fontSize: 24 }} />
                <Typography
                  variant="subtitle1"
                  sx={{ color: '#263238', fontWeight: 600 }}
                >
                  Diyetisyen Bilgileri
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                  },
                  gap: 3,
                }}
              >
                <TextField
                  fullWidth
                  label="Diyetisyen Adı"
                  value={client.dietitianName || 'Belirtilmemiş'}
                  disabled
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                      },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Diyetisyen E-posta"
                  value={client.dietitianEmail || 'Belirtilmemiş'}
                  disabled
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#ffffff',
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                      },
                    },
                  }}
                />
              </Box>
            </Paper>

            {/* Kaydet/İptal Butonları */}
            {isEditing && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-end',
                  pt: 2,
                }}
              >
                <CancelButton onClick={handleCancel} />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    bgcolor: '#43A047',
                    '&:hover': {
                      bgcolor: '#388E3C',
                    },
                  }}
                >
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {!loading && activeTab === 'nutrition' && (
          <Box
            sx={{
              minHeight: '550px',
              animation: 'fadeIn 0.3s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6">Tarih Seç:</Typography>
                <TextField
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Box>
            </Paper>
            <NutritionTracker
              clientId={client.id}
              clientName={`${client.firstName} ${client.lastName}`}
              selectedDate={selectedDate}
              isDietitian={false}
              canAddEditMeals={false}
              client={client}
            />
          </Box>
        )}

        {!loading && activeTab === 'weight' && (
          <Box
            sx={{
              minHeight: '550px',
              animation: 'fadeIn 0.3s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <WeightTracker
              clientId={client.id}
              clientName={`${client.firstName} ${client.lastName}`}
              client={client}
              isDietitian={true}
              dietitianId={dietitianId ?? undefined}
              dietitianName={dietitian ? `${dietitian.firstName} ${dietitian.lastName}` : undefined}
            />
          </Box>
        )}

        {!loading && activeTab === 'appointments' && (
          <Box
            sx={{
              minHeight: '550px',
              animation: 'fadeIn 0.3s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Randevular ({appointments.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {appointments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Henüz randevu bulunmuyor.
              </Typography>
            ) : (
              <Box>
                {appointments
                  .sort(
                    (a, b) =>
                      new Date(b.appointmentDate).getTime() -
                      new Date(a.appointmentDate).getTime()
                  )
                  .map((appointment) => (
                    <Paper
                      key={appointment.id}
                      elevation={1}
                      sx={{ p: 2, mb: 2 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {new Date(
                              appointment.appointmentDate
                            ).toLocaleDateString('tr-TR')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {appointment.startTime} - {appointment.endTime}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Tür:</strong> {appointment.type} •{' '}
                            <strong>Konum:</strong> {appointment.location}
                          </Typography>
                          {appointment.fee && (
                            <Typography variant="body2">
                              <strong>Ücret:</strong> ₺{appointment.fee}
                              {appointment.isPaid && (
                                <Chip
                                  label="Ödendi"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                          )}
                          {appointment.notes && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Notlar:</strong> {appointment.notes}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={appointment.status}
                          sx={{
                            bgcolor:
                              appointment.status === 'Tamamlandı'
                                ? '#43A047'
                                : appointment.status === 'Planlandı'
                                ? '#2E7D32'
                                : '#607D8B',
                            color: 'white',
                          }}
                        />
                      </Box>
                    </Paper>
                  ))}
              </Box>
            )}
          </Box>
        )}

        {!loading && activeTab === 'programs' && (
          <Box
            sx={{
              minHeight: '550px',
              animation: 'fadeIn 0.3s ease-in',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'translateY(10px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Diyet Programları ({dietPrograms.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {dietPrograms.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Henüz diyet programı bulunmuyor.
              </Typography>
            ) : (
              <Box>
                {dietPrograms
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((program) => (
                    <Paper
                      key={program.id}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 2,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s, background-color 0.2s',
                        '&:hover': {
                          boxShadow: 3,
                          bgcolor: 'action.hover',
                        },
                      }}
                      onClick={() => {
                        onClose();
                        navigate(`/dietitian/programs/${program.id}`);
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {program.programName}
                        </Typography>
                        <Chip
                          label={program.status}
                          size="small"
                          sx={{
                            bgcolor:
                              program.status === 'Aktif'
                                ? '#43A047'
                                : '#607D8B',
                            color: 'white',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {program.programType} •{' '}
                        {new Date(program.createdAt).toLocaleDateString(
                          'tr-TR'
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Başlangıç:</strong>{' '}
                        {new Date(program.startDate).toLocaleDateString(
                          'tr-TR'
                        )}{' '}
                        • <strong>Bitiş:</strong>{' '}
                        {new Date(program.endDate).toLocaleDateString('tr-TR')}
                      </Typography>
                      {program.targetWeight && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Hedef Kilo:</strong> {program.targetWeight} kg
                          {program.currentWeight &&
                            ` • Mevcut Kilo: ${program.currentWeight} kg`}
                        </Typography>
                      )}
                      {program.description && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Açıklama:</strong> {program.description}
                        </Typography>
                      )}
                    </Paper>
                  ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>

    {/* BMR formül katsayılarını düzenleme dialogu */}
    <Dialog
      open={formulaEditDialogOpen}
      onClose={() => setFormulaEditDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5, px: 2 }}>
        BMR formülünü düzenle
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5, px: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          BMR = (kilo kats.)×kilo + (boy kats.)×boy + (yaş kats.)×yaş + sabit — erkek ve kadın ayrı.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Hazır formül yükle</InputLabel>
          <Select
            label="Hazır formül yükle"
            value={selectedPresetInDialog ?? ''}
            onChange={(e) => applyPresetToEdit(e.target.value)}
          >
            <MenuItem value="">Seçiniz veya aşağıdan katsayıları düzenleyin</MenuItem>
            <MenuItem value="mifflin">Mifflin-St Jeor (1990)</MenuItem>
            <MenuItem value="harris-benedict">Harris-Benedict Orijinal (1919)</MenuItem>
            <MenuItem value="harris-benedict-revised">Harris-Benedict Revize (1984)</MenuItem>
          </Select>
        </FormControl>
        {selectedPresetInDialog && BMR_FORMULA_EQUATIONS[selectedPresetInDialog] && (
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" gutterBottom>
              Denklem (bilgi):
            </Typography>
            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              Erkek: {BMR_FORMULA_EQUATIONS[selectedPresetInDialog].erkek}
            </Typography>
            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mt: 0.5 }}>
              Kadın: {BMR_FORMULA_EQUATIONS[selectedPresetInDialog].kadın}
            </Typography>
          </Paper>
        )}
        <Stack spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(33, 150, 243, 0.04)' }}>
            <Typography variant="caption" fontWeight={600} color="primary" gutterBottom display="block">Erkek</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField size="small" label="Kilo katsayısı" type="number" value={editCoeffs.weightCoefMale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, weightCoefMale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="Boy katsayısı" type="number" value={editCoeffs.heightCoefMale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, heightCoefMale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="Yaş katsayısı" type="number" value={editCoeffs.ageCoefMale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, ageCoefMale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="Sabit" type="number" value={editCoeffs.constantMale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, constantMale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
            </Box>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(233, 30, 99, 0.04)' }}>
            <Typography variant="caption" fontWeight={600} sx={{ color: '#E91E63' }} gutterBottom display="block">Kadın</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField size="small" label="Kilo katsayısı" type="number" value={editCoeffs.weightCoefFemale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, weightCoefFemale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="Boy katsayısı" type="number" value={editCoeffs.heightCoefFemale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, heightCoefFemale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="Yaş katsayısı" type="number" value={editCoeffs.ageCoefFemale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, ageCoefFemale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="Sabit" type="number" value={editCoeffs.constantFemale} onChange={(e) => { setSelectedPresetInDialog(null); setEditCoeffs((c) => ({ ...c, constantFemale: parseFloat(e.target.value) || 0 })); }} inputProps={{ step: 0.01 }} />
            </Box>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <CancelButton onClick={() => setFormulaEditDialogOpen(false)} size="small" />
        <Button variant="contained" onClick={saveFormulaEdit} sx={{ bgcolor: '#FF9800', '&:hover': { bgcolor: '#F57C00' } }}>Uygula</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ClientDetailModal;
